import {
  Reducer, useCallback, useEffect, useReducer, useRef, useState,
} from 'react';

import Image from 'next/image';
import { useRouter } from 'next/router';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import ChatContainer from '@/components/chat/chat-container';
import { resolveStaleAgeMs } from '@/components/map/bus-motion';
import Gmap from '@/components/map/gmap';
import { SET_TOKEN, TRACKING_CHANNEL_UPDATE } from '@/global-states/constants';
import { ReducerState as SocketReducerState } from '@/global-states/socket';
import {
  getTrip,
  getTripWithId,
  ReducerState as TripReducerState,
} from '@/global-states/trip';
import Container from '@/layout/container';
import Footer from '@/layout/footer';
import GmapContainer from '@/layout/gmap-container';
import Header from '@/layout/header';
import HeaderContainer from '@/layout/header-container';
import EtaCard from '@/shared/eta-card';
import PositionCard from '@/shared/position/position-card';
import DriverCard from '@/shared/profile/driver-card';
import ActionReducer, {
  ReducerState as ReducerSocket,
  initialState as SocketInitialState,
  UPDATE_BUS_LIST,
  UPDATE_SCHEDULE_LIST,
} from '@/socket/reducer';
import imageLoader from '@/utils/image-loader';
import grayLogo from 'public/assets/images/logo_gray.svg';

const Index = () => {
  const [socketState, socketDispatch] = useReducer<Reducer<ReducerSocket, any>>(
    ActionReducer,
    SocketInitialState
  );
  const dispatch = useDispatch();
  const router = useRouter();
  const { query = { jwt: '', id: '' } } = !!router && router;
  const { jwt, id } = query;
  const {
    token,
    tripData,
    // tripDataIsLoading,
    // tripDataIsSuccess,
    // tripDataIsError,
  } = useSelector(
    ({
      trip,
      socket,
    }: {
      trip: TripReducerState;
      socket: SocketReducerState;
    }) => ({
      token: trip?.token,
      tripData: trip?.tripList,
      socketData: socket,
      tripDataIsLoading: trip.tripListState === 'loading',
      tripDataIsSuccess: trip.tripListState === 'success',
      tripDataIsError: trip.tripListState === 'error',
    }),
    shallowEqual
  );

  const { busList } = socketState;
  const isEnded = !!tripData?.trip?.is_ended;

  // One ticker, one source of truth. Two independent timers would drift and
  // could show a faded marker beside a still-blinking LIVE pill.
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!busList.length || isEnded) return undefined;
    const tickerId = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tickerId);
  }, [busList.length, isEnded]);

  // When the bus list last changed identity. Every socket tick dispatches a
  // freshly built array, so this answers "did anything arrive recently?" — the
  // half of the freshness signal that survives the socket merge branch which
  // forwards a new position without refreshing serverTimestamp.
  const lastBusChangeAtRef = useRef<number>(-Infinity);
  useEffect(() => {
    lastBusChangeAtRef.current = busList.length ? Date.now() : -Infinity;
  }, [busList]);

  const staleAgeMs = resolveStaleAgeMs(
    busList[0]?.serverTimestamp,
    lastBusChangeAtRef.current,
    nowMs
  );

  useEffect(() => {
    dispatch({ type: SET_TOKEN, payload: jwt });
  }, [dispatch, jwt]);

  useEffect(() => {
    if (token) {
      (async () => {
        dispatch(getTrip(token));
      })();
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (id) {
      (async () => {
        dispatch(getTripWithId(id as string));
      })();
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (tripData?.trip?.socket_channel) {
      dispatch({
        type: TRACKING_CHANNEL_UPDATE,
        payload: tripData?.trip?.socket_channel,
      });
    }

    if (tripData?.trip?.is_ended) {
      dispatch({
        type: TRACKING_CHANNEL_UPDATE,
        payload: null,
      });
      // @ts-ignore
      socketState.socket?.emit('leave_channel', tripData?.trip?.socket_channel);
      socketDispatch({ type: `${UPDATE_BUS_LIST}_RESET` });
      socketDispatch({ type: `${UPDATE_SCHEDULE_LIST}_RESET` });
    }
  }, [dispatch, socketState.socket, tripData]);

  const handleRefresh = useCallback(async () => {
    if (jwt) dispatch(getTrip(token));
    if (id) dispatch(getTripWithId(id as string));
  }, [dispatch, id, jwt, token]);

  return (
    <>
      <Container>
        <GmapContainer>
          <Gmap
            data={tripData?.trip?.stops}
            stops={tripData?.trip?.stops}
            socketState={socketState}
            socketDispatch={socketDispatch}
            staleAgeMs={staleAgeMs}
          />
        </GmapContainer>

        <Header
          live={busList.length > 0}
          ended={isEnded}
          stale={staleAgeMs !== undefined}
        />

        <HeaderContainer
          floating={
            tripData ? (
              <EtaCard
                variant="pill"
                tripStops={tripData?.trip?.stops}
                busList={busList[0]}
              />
            ) : null
          }
          footer={
            <div className="hidden lg:block">
              <Footer variant="panel" />
            </div>
          }
          peek={
            tripData ? (
              <DriverCard
                userName={tripData?.user?.nickname}
                driverName={tripData?.driver?.name}
                driverPicture={tripData?.driver?.profile_picture_url}
                driverPlate={
                  busList[0]?.vehicleId ?? tripData?.driver?.vehicle_plate
                }
                tripStops={tripData?.trip?.stops}
                etaSlot={
                  <div className="hidden lg:block">
                    <EtaCard
                      variant="card"
                      tripStops={tripData?.trip?.stops}
                      busList={busList[0]}
                    />
                  </div>
                }
              />
            ) : (
              <div className="flex flex-row items-center gap-4 py-4 lg:py-2">
                <Image
                  loader={imageLoader}
                  src={grayLogo}
                  alt="RushTrail"
                  width={72}
                  height={72}
                  priority
                  className="aspect-auto"
                />
                <p className="text-[15px] font-bold leading-snug text-ro-stone lg:text-base">
                  Trip might have ended or not available
                </p>
              </div>
            )
          }
        >
          {tripData ? (
            <>
              <PositionCard
                tripStops={tripData?.trip?.stops}
                busList={busList[0]}
                handleRefresh={handleRefresh}
              />
              <div className="lg:hidden">
                <Footer variant="sheet" />
              </div>
            </>
          ) : null}
        </HeaderContainer>
      </Container>

      <ChatContainer />
    </>
  );
};

export default Index;
